// ============================================================================
// OperacionalDestinatarios.js
// Tela do Operacional: gerir QUEM recebe o e-mail (com o PDF anexo) quando uma
// avaliação é FINALIZADA. A lista de EXTRAS é SEPARADA POR AVALIAÇÃO (seletor no
// topo) — Avaliação 360, Autoverificação, Auditoria Financeira/Estoques e
// Avaliação de Segurança do Alimento. Adicionar usuário, ativar/inativar (nunca
// apaga — histórico preservado).
//
// ⚠️ Estes são só os EXTRAS. Além deles, SEMPRE recebem (automático, não
// aparece aqui): o(s) gerente(s) da loja avaliada e todos os usuários com perfil
// 'Operacional'. O aviso azul abaixo explica isso ao usuário.
//
// Visual no padrão do site (kit compartilhado psUI), na cor do módulo (#475569).
// ============================================================================

import { useEffect, useState, useCallback } from 'react';
import { Mail, UserPlus, Power, PowerOff, AlertCircle, Info } from 'lucide-react';
import { estilos, BadgeStatus } from '../../processoseletivo/psUI';

const COR = '#475569';

// Avaliações do módulo = os valores de APLICACAO aceitos pelo backend
// (server/operacional/destinatarios-email.js › APLICACOES). Rótulos iguais aos
// cards do hub.
const AVALIACOES = [
  { id: 'aval360', label: 'Avaliação 360' },
  { id: 'autoavaliacao', label: 'Autoverificação de Fechamento Mensal' },
  { id: 'checklist', label: 'Auditoria Financeira e de Estoques' },
  { id: 'sa', label: 'Avaliação de Segurança do Alimento' },
];

function OperacionalDestinatarios() {
  const [aplicacao, setAplicacao] = useState(AVALIACOES[0].id);
  const [destinatarios, setDestinatarios] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [novoUsuarioId, setNovoUsuarioId] = useState('');
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const token = localStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };
      const [rd, ru] = await Promise.all([
        fetch(`/api/operacional/destinatarios/destinatarios?aplicacao=${encodeURIComponent(aplicacao)}`, { headers }),
        fetch('/api/operacional/destinatarios/usuarios', { headers }),
      ]);
      if (!rd.ok || !ru.ok) throw new Error('Falha ao carregar dados.');
      setDestinatarios(await rd.json());
      setUsuarios(await ru.json());
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, [aplicacao]);

  useEffect(() => { carregar(); }, [carregar]);

  const adicionar = async () => {
    if (!novoUsuarioId) return;
    setSalvando(true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch('/api/operacional/destinatarios/destinatarios', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ aplicacao, usuarioId: Number(novoUsuarioId) }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.message || 'Erro ao adicionar.');
      }
      setNovoUsuarioId('');
      await carregar();
    } catch (e) {
      alert(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const alternar = async (id, ativoAtual) => {
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`/api/operacional/destinatarios/destinatarios/${id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ aplicacao, ativo: ativoAtual ? 0 : 1 }),
      });
      if (!resp.ok) throw new Error('Erro ao atualizar.');
      await carregar();
    } catch (e) {
      alert(e.message);
    }
  };

  // Usuários que ainda não estão na lista desta avaliação (seletor de adicionar).
  const idsNaLista = new Set(destinatarios.map((d) => d.USUARIO_ID));
  const disponiveis = usuarios.filter((u) => !idsNaLista.has(u.ID));

  const input = { padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, color: '#0f172a', minWidth: 280 };

  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ background: `${COR}15`, color: COR, padding: 12, borderRadius: 12, display: 'flex' }}>
          <Mail size={24} />
        </div>
        <div>
          <h3 style={estilos.tituloSecao}>Destinatários dos avisos</h3>
          <p style={estilos.subtituloSecao}>Quem recebe o e-mail com o PDF quando uma avaliação é finalizada.</p>
        </div>
      </div>

      {/* Aviso: destinatários automáticos (não aparecem na lista). */}
      <div style={{
        display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', marginBottom: 18,
        background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 10, color: '#334155', fontSize: 13, lineHeight: 1.5,
      }}>
        <Info size={18} color={COR} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          Além dos cadastrados abaixo, <strong>sempre</strong> recebem automaticamente: o(s) <strong>gerente(s) da loja avaliada</strong> e todos os usuários com perfil <strong>Operacional</strong>. A lista abaixo são os <strong>extras</strong>, e é <strong>separada por avaliação</strong>.
        </div>
      </div>

      {/* Seletor de avaliação (cada uma tem a sua lista de extras). */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6 }}>Avaliação</label>
        <select style={{ ...input, minWidth: 360 }} value={aplicacao} onChange={(e) => { setAplicacao(e.target.value); setNovoUsuarioId(''); }}>
          {AVALIACOES.map((a) => (
            <option key={a.id} value={a.id}>{a.label}</option>
          ))}
        </select>
      </div>

      {/* Adicionar extra à avaliação selecionada. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={input} value={novoUsuarioId} onChange={(e) => setNovoUsuarioId(e.target.value)}>
          <option value="">— Escolha usuário para adicionar —</option>
          {disponiveis.map((u) => (
            <option key={u.ID} value={u.ID}>{u.NOME_COMPLETO} · {u.PERFIL}</option>
          ))}
        </select>
        <button
          onClick={adicionar}
          disabled={!novoUsuarioId || salvando}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 8, border: 'none',
            fontWeight: 700, fontSize: 14, color: '#fff',
            background: novoUsuarioId && !salvando ? COR : '#cbd5e1',
            cursor: novoUsuarioId && !salvando ? 'pointer' : 'not-allowed',
          }}
        >
          <UserPlus size={16} /> Adicionar
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 24, color: '#64748b' }}>Carregando…</div>
      ) : erro ? (
        <div style={{ padding: 24, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}><AlertCircle size={18} /> {erro}</div>
      ) : (
        <div style={{ ...estilos.tabelaWrap, overflowX: 'auto' }}>
          <table style={{ ...estilos.tabela, minWidth: 560 }}>
            <thead>
              <tr>
                <th style={estilos.th}>Usuário</th>
                <th style={estilos.th}>E-mail</th>
                <th style={estilos.th}>Perfil</th>
                <th style={estilos.th}>Situação</th>
                <th style={{ ...estilos.th, textAlign: 'right' }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {destinatarios.map((d) => (
                <tr key={d.ID} style={{ opacity: d.ATIVO ? 1 : 0.6 }}>
                  <td style={estilos.td}><strong>{d.NOME_COMPLETO}</strong></td>
                  <td style={estilos.td}>{d.EMAIL || '—'}</td>
                  <td style={estilos.td}>{d.PERFIL}</td>
                  <td style={estilos.td}>
                    <BadgeStatus tom={d.ATIVO ? 'aprovado' : 'neutro'}>{d.ATIVO ? 'Ativo' : 'Inativo'}</BadgeStatus>
                  </td>
                  <td style={{ ...estilos.td, textAlign: 'right' }}>
                    <button
                      onClick={() => alternar(d.ID, d.ATIVO)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8,
                        border: `1px solid ${d.ATIVO ? '#e2e8f0' : COR}`, background: '#fff',
                        color: d.ATIVO ? '#64748b' : COR, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                      }}
                    >
                      {d.ATIVO ? <><PowerOff size={14} /> Inativar</> : <><Power size={14} /> Ativar</>}
                    </button>
                  </td>
                </tr>
              ))}
              {destinatarios.length === 0 && (
                <tr><td style={{ ...estilos.td, textAlign: 'center', color: '#94a3b8' }} colSpan={5}>Nenhum extra cadastrado para esta avaliação.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default OperacionalDestinatarios;
